<template>
  <MglMap
    ref="map"
    :access-token="accessToken"
    :map-style="style"
    :max-zoom="10"
    :min-zoom="2"
    @load="onMapLoad"
  >
    <VesselMarker
      v-for="vessel in filteredFleet"
      :key="vessel.idVessel"
      :vessel="vessel"
      :on-pressed="onClickMarker"
      :show-fouling-status="showFoulingStatus"
    />

    <MglFullscreenControl position="top-right" />
    <MglNavigationControl position="top-right" />

    <VesselFilterControl
      position="top-right"
      :on-filter-change="handleFilterChange"
    />

    <FoulingToggleControl
      position="top-right"
      :on-toggle="handleFoulingToggle"
      :initial-state="true"
    />

    <ToolbarComponent
      class="tool-bar-position"
      :on-meteo-selected="onLoadWeatherTilesPressed"
      :on-reload-button-pressed="onLoadWeatherTilesPressed"
      :on-switch-changed="handleWeatherSwitchChanged"
      :show-diagram="false"
    />

    <!--  SPECIAL ZONES LAYERS -->
    <Ecas />

    <div
      v-if="fetchFleetLoading"
      class="loadingContainer"
    >
      <span class="loadingText">Loading fleet...</span>
      <v-progress-circular
        indeterminate
        class="loading"
        :size="100"
        :width="4"
      />
    </div>
  </MglMap>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import Mapbox from "mapbox-gl";
import { MglMap, MglFullscreenControl, MglNavigationControl } from "vue-mapbox";
import VesselMarker from "./components/VesselMarker.vue";
import VesselFilterControl from "./components/VesselFilterControl.vue";
import FoulingToggleControl from "./components/FoulingToggleControl.vue";
import ToolbarComponent from "../../weather_routing/map/components/ToolbarComponent.vue";
import Ecas from "../../weather_routing/map/layers/ecas.vue";

import { windOptionToInt } from "../../../domain/weather_routing/meteo_options/enum";

export default {
  name: "HomeMap",
  components: {
    MglMap,
    MglFullscreenControl,
    MglNavigationControl,
    VesselMarker,
    VesselFilterControl,
    FoulingToggleControl,
    ToolbarComponent,
    Ecas,
  },

  data() {
    return {
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      style: process.env.VUE_APP_MAPBOX_STYLE,
      weatherTilesVisible: false,

      activeFilters: {
        low: true,
        medium: true,
        high: true,
        critical: true,
        unknown: true,

      },
      showFoulingStatus: true,
    };
  },

  beforeCreate() {
    this.$store.dispatch("main/fetchFleet");
  },

  async created() {
    this.mapbox = Mapbox;
  },

  computed: {
    ...mapState("main", ["fleet", "selectedVessel", "fetchFleetLoading"]),
    ...mapGetters("main", ["flyToCoordinates"]),

    filteredFleet() {
      if (!this.fleet) return [];

      return this.fleet.filter((vessel) => {
        const fouling = vessel.fouling;

        if (fouling === undefined || fouling === null) {
          return this.activeFilters.unknown;
        }

        if (fouling <= 1) {
          return this.activeFilters.low;
        } else if (fouling < 4) {
          return this.activeFilters.medium;
        } else if (fouling < 8) {
          return this.activeFilters.high;
        } else {
          return this.activeFilters.critical;
        }
      });
    },
  },

  methods: {

    ...mapActions("weatherRouting/tilesModule", [
      "removeCurrentLayer",
      "loadTile",
    ]),

    ...mapActions("weatherRouting/specialZones", [
      "loadZone"
    ]),

    handleWeatherSwitchChanged(showWeather, option) {
      this.weatherTilesVisible = showWeather;

      if (!showWeather) {
        this.removeCurrentLayer({
          map: this.$refs.map.map,
        });
      } else {
        this.onLoadWeatherTilesPressed(option);
      }
    },

    onMapLoad(event) {

      const flyTo = event.component.actions.flyTo;
      flyTo({
        center: this.flyToCoordinates,
        zoom: 3,
        bearing: 0,
        pitch: 0,
      });
    },

    fetchFleet() {
      this.$store.dispatch("main/fetchFleet");
    },

    onClickMarker(vessel) {
      this.$store.commit("main/SET_SELECTED_VESSEL", vessel);
      this.$store.commit("main/SET_DRAWER_OPEN", true);
    },

    handleFilterChange(filters) {
      this.activeFilters = { ...filters };
    },

    handleFoulingToggle(state) {
      this.showFoulingStatus = state;
    },

    async onLoadWeatherTilesPressed(option) {
      const payload = {
        map: this.$refs.map.map,
        sourceIdBase: "weather-tile-source",
        layerIdBase: "weather-tile-layer",
        meteo: windOptionToInt(option),
      };
      await this.loadTile(payload);
    },

    onShowWeatherTilesChanged(showWeather, option) {
      if (!showWeather) {
        this.removeCurrentLayer({
          map: this.$refs.map.map,
        });
      } else {
        this.onLoadWeatherTilesPressed(option);
      }
    },

  }

};
</script>

<style scoped>
.loadingContainer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: var(--gradient-overlay);
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.loading {
  color: var(--color-text) !important;
}

.loadingText {
  color: var(--color-text) !important;
  font-size: 20px;
  font-weight: 600;
}

</style>
